import React from "react";
import PropTypes from "prop-types";
import ReactModal from "react-modal";
import styled from "styled-components";
import { MODAL_STYLES } from "../../constants";

const ModalHeader = styled.div`
  background: #CE0E2D;
  color: white;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 700;
  font-size: 1em;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.4em;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  opacity: 0.8;
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
  }
`;

const ModalBody = styled.div`
  padding: 20px 20px 24px;
`;

const BodyText = styled.p`
  margin: 0;
  line-height: 1.6;
  font-size: 0.95em;
  color: #333;
  text-align: center;
`;

const WarningNote = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-left: 4px solid var(--clr-red-dark);
  border-radius: 6px;
  padding: 10px 14px;
  margin-top: 14px;
  font-size: 0.85em;
  font-weight: 600;
  color: #7f1d1d;
  text-align: center;
`;

const ButtonRow = styled.div`
  padding: 12px 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: center;
  gap: 24px;
`;

const ConfirmBtn = styled.button`
  background: #CE0E2D;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 7px 18px;
  font-size: 0.85em;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #a30b24;
  }
`;

const CancelBtn = styled.button`
  background: white;
  color: #555;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 7px 18px;
  font-size: 0.85em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #f3f4f6;
    border-color: #999;
    color: #333;
  }
`;

/**
 * Modal component for refresh confirmation
 */
const RefreshModal = React.memo(({ isOpen, onConfirm, onCancel, hasUnsavedChanges }) => {
  return (
    <ReactModal
      isOpen={isOpen}
      style={MODAL_STYLES}
      onRequestClose={onCancel}
      appElement={document.getElementById("root")}
    >
      <ModalHeader>
        Fetch Latest Data
        <CloseButton onClick={onCancel}>&times;</CloseButton>
      </ModalHeader>
      <ModalBody>
        <BodyText>
          This will pull the most current data from the server and refresh the
          table with the latest table data.
        </BodyText>
        {hasUnsavedChanges && (
          <WarningNote>
            You have unsaved changes. Fetching latest data will discard any
            changes you have not saved yet.
          </WarningNote>
        )}
      </ModalBody>
      <ButtonRow>
        <CancelBtn onClick={onCancel}>Cancel</CancelBtn>
        <ConfirmBtn onClick={onConfirm}>Yes, Fetch Latest</ConfirmBtn>
      </ButtonRow>
    </ReactModal>
  );
});

RefreshModal.displayName = "RefreshModal";

RefreshModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  hasUnsavedChanges: PropTypes.bool,
};

export default RefreshModal;
